/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import {
  getFirstDayofSelectedMonth,
  isObjectOrArrayAndEmpty
} from '../../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import withMSLoadingStatus from '../../../../shared/MSStatusWrappers/MSLoadingStatus';
import withMSErrorStatus from '../../../../shared/MSStatusWrappers/MSErrorStatus';
import useMounted from '../../../../shared/useMount/useMount';
import CarbonTabComponent from '../SiteCarbonComponent/SiteCarbonComponent';
import ICarbonEmissionHOCProps from '../../../CarbonEmissionKPI/CarbonEmissionHOC/ICarbonEmissionHOCProps';
import { apiNoContentStatusCode } from '../../../../shared/Utils/Constants';
import { ICarbonData, ISiteCard } from '../../../IEnergyManagementProps';
import { getCarbonEndDateBasedOnSub } from '../../../../shared/Utils/CarbonViewDetailsUtils';

interface ISiteCarbonHOCContent extends ISiteCard {
  data: ICarbonData;
  displayBaseline?: boolean;
}

const SiteCarbonHOCContent = (props: ISiteCarbonHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, displayBaseline } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      <CarbonTabComponent data={data} displayBaseline={displayBaseline} />
    </div>
  );
};

const CarbonTabHOCWithLoader = withMSLoadingStatus(
  withMSErrorStatus(SiteCarbonHOCContent)
);

const SiteCarbonHOC = (props: ICarbonEmissionHOCProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    bearerToken,
    requestContext,
    toggleFeatureScopeOne,
    displayBaseline
  } = props;
  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );

  const getAdjustedEndDate = (userEndDate: string | Date) => {
    // Parse the user's end date
    const userDate = new Date(userEndDate);
    const currentDate = new Date();

    // Check if the user's end date month and year are the same as the current month and year
    if (
      userDate.getMonth() === currentDate.getMonth() &&
      userDate.getFullYear() === currentDate.getFullYear()
    ) {
      // Subtract one month from the user's end date
      userDate.setMonth(userDate.getMonth() - 1);
    }

    return userDate.toISOString();
  };

  const endTimeNewFormat = getCarbonEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    'utilitymeter'
  );

  const carbonConnectorConfig = {
    name: toggleFeatureScopeOne ? 'site-carbon-new' : 'carbon-site',
    options: {
      startDate: startTime,
      endDate: endTimeNewFormat,
      dataSource: ''
    }
  };

  const carbonConnectorConfigV4 = {
    name: toggleFeatureScopeOne ? 'site-carbon-new' : 'carbon-site',
    options: {
      scopeType: 'all',
      startDate: startTime,
      endDate: endTimeNewFormat,
      dataSource: 'utilitymeter'
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig: carbonConnectorConfigV4,
    requestContext,
    bearerToken
  });

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [requestContext]);

  const { all, loading, error, status } = dataConnector.data;
  const CarbonTabSite = (all as unknown) as ICarbonData;
  const isDataAvailable = CarbonTabSite ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [errorInfo, setErrorInfo] = useState(error);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [data, setData] = useState({} as ICarbonData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(CarbonTabSite);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <CarbonTabHOCWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={statusCode}
      onRefresh={onRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      isNoContent={status === apiNoContentStatusCode}
      displayBaseline={displayBaseline}
    />
  );
};
export default SiteCarbonHOC;
